import { useProjectParams } from '@hooks/useProjectParams';
import { ProjectStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { ProjectPageLayout } from 'pages/Project/Common/Layouts/ProjectPageLayout';

import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { AddOrEditAgreementModal } from '../AddOrEditAgreementModal';
import { AgreementsRegistryTable } from '../AgreementsRegistryTable';
import { AgreementsRegistryToolbar } from '../AgreementsRegistryToolbar';
import { useGetAgreementRegistry } from '../hooks/useGetAgreementRegistry';

import './AgreementsRegistry.css';

export const AgreementsRegistry = () => {
    const { state } = useLocation();
    const { id: projectId, methodology } = useProjectParams();
    const { projectData, refreshProject } = useProjectData(projectId);
    const contractId = projectData?.contractId;
    const contractHasContractChanges =
        projectData?.contractNavigation?.data?.contractNavData?.metadata?.hasContractChanges;

    const statusContract = projectData?.contractNavigation?.data?.contractNavData?.status;

    const isProjectStatusDelayed = projectData?.status === ProjectStatusEnum.Delayed;
    const isProjectStatusCompleted = projectData?.status === ProjectStatusEnum.Completed;

    const isDisabledByProjectStatus = isProjectStatusDelayed || isProjectStatusCompleted;

    const isCreatingFromContract = state?.createFromContract;
    const [isCreateOrEditModalOpened, setIsCreateOrEditModalOpened] = useState(() => isCreatingFromContract || false);

    const {
        dataSource,
        handleSearch,
        refreshData,
        isRegistryLoading,
        changeFilterHandler,
        hasActiveFilters,
        isLastItem,
        gridApiRef,
        key,
        agreementData,
    } = useGetAgreementRegistry({
        contractId,
        contractHasContractChanges,
    });

    const breadCrumbsItems = useMemo(
        () => [
            {
                label: projectData?.name,
                href: `/projects/${methodology}/${projectData?.uniqueId}`,
            },
            {
                label: 'Договор',
                href: `/projects/${methodology}/${projectData?.uniqueId}/contract`,
            },
        ],
        [methodology, projectData?.uniqueId, projectData?.name],
    );

    const createOrEditAgreementModalHandler = useCallback((nextState) => {
        setIsCreateOrEditModalOpened(nextState);
    }, []);

    const disabledSearch = useMemo(() => !agreementData.length, [agreementData.length]);

    return (
        <div id="AgreementsRegistry">
            <ProjectPageLayout title="Реестр доп. соглашений" breadCrumbsItems={breadCrumbsItems}>
                <div className="wrapperToolbarLine padding-xl">
                    <AgreementsRegistryToolbar
                        createAgreement={createOrEditAgreementModalHandler}
                        isLoadingInProgress={isRegistryLoading}
                        handleSearch={handleSearch}
                        hasActiveFilters={hasActiveFilters}
                        isDisabledByProjectStatus={isDisabledByProjectStatus}
                        statusContract={statusContract}
                        disabled={disabledSearch}
                    />
                </div>
                <div className="main">
                    <AgreementsRegistryTable
                        dataSource={dataSource}
                        refreshDataHandler={refreshData}
                        refreshProject={refreshProject}
                        currentContractId={contractId}
                        changeFilterHandler={changeFilterHandler}
                        isLastItem={isLastItem}
                        ref={gridApiRef}
                        isDisabledByProjectStatus={isDisabledByProjectStatus}
                        statusContract={statusContract}
                        contractHasContractChanges={contractHasContractChanges}
                        key={key}
                    />
                </div>
                {isCreateOrEditModalOpened && (
                    <AddOrEditAgreementModal
                        currentContractId={contractId}
                        isModalOpened={isCreateOrEditModalOpened}
                        closeHandler={createOrEditAgreementModalHandler}
                        refreshHandler={refreshData}
                        refreshProject={refreshProject}
                        contractHasContractChanges={contractHasContractChanges}
                        isCreating
                    />
                )}
            </ProjectPageLayout>
        </div>
    );
};

AgreementsRegistry.displayName = 'AgreementsRegistry';
